<template>
  <main class="main main--about">
    <div class="about__sidebar">
      <div class="about__menu">
        <ul>
          <li
            v-for="(item, index) in menu"
            :key="index"
            :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
            :class="[
              {
                'about__menu--active' : index === selected
              }
            ]"

            @click="scrollToDiv(item)"
          >
            {{ item.label }}
          </li>
        </ul>
      </div>
      <!-- <img :src="images.sidebar" alt="" class="sidebar__image"> -->
    </div>
    <div class="group group--wide group--first">
      <div class="about">
        <div class="about__content">
          <section class="about__header" id="overview" ref="overview">
            <div class="about__intro">
              <h1 
                :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
                class="h1 mt0">{{ org.program_name }} connects you with qualified experts who can help</h1>
              <p class="p">
                Everything in your life affects how you feel. When you’re healthier, you’re happier. When you’ve got friends and family you can count on, you feel less lonely. And when your finances are in order, life isn’t so stressful.
              </p>
              <p class="p">We match you with an expert based on your unique situation. You’ll get personalized guidance and helpful advice you can use right away to navigate life events and reach personal goals. All it takes is a single conversation to get things going.</p>
            </div>
            <img class="about__img hide-below-medium" :src="images.header" alt="">
          </section>
          <section ref="help" id="help">
            <div 
              class="about-block"
              v-for="(section, key ) in aboutSections"
              :key="key"
            >
              <div>
                <div class="about-block__header">
                  <h4 
                    :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
                    class="h4 small mt0" 
                    v-html="section.subtitle"/>
                  <h2 
                    :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
                    class="h2 mt0" 
                    v-html="section.heading" />
                  <p class="p mb0" v-html="section.copy" />
                </div>
                <div class="about-block__help-list">
                  <module-help-list :items="section.helpList" />
                </div>
              </div>
              <div class="about-block__image">
                <img class=" about__img" v-if="section.image" :src="section.image" alt="" >
              </div>
              <module-info-list :sections="section.iconList" :compact="true"/>
            </div>
            
          </section> 
        </div>
       
        <div class="about__things" id="thingsToKnow" ref="thingsToKnow">
          <h2 
            :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
            class="h1">Some other things to know about how this works</h2>
          <div class="about__grid">
            <div
              v-for="(section, index) in thingsToKnow"
              :key="index"

              class="things__section"
            >
              <h3 
                :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
                class="h3">{{ section.title }}</h3>
              <p class="p">{{ section.copy }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
// Import
import { mapGetters } from 'vuex';

// Utils
import { arrayLoop } from 'utils/array.js';

// Modules
import ModuleInfoList from 'components/InfoList';
import ModuleHelpList from 'components/HelpList';

import header from 'images/about/header-illustration.png';
import legal from 'images/about/legal.png';
import family from 'images/about/family.png';
import financial from 'images/about/financial.png';
import emotional from 'images/about/emotional.png';
import sidebar from 'images/about/sidebar.png';

// Images
const images = {
  header,
  legal,
  family,
  financial,
  emotional,
  sidebar,
};

// Export
export default {

  // Components
  components: {
    ModuleInfoList,
    ModuleHelpList,
  },

  // Data
  data() {
    return {
      images,
      selected: 0,

      // Side Menu
      menu: [
        {
          id: 0,
          label: 'Overview',
          link: 'overview',
        },
        {
          id: 1,
          label: 'What we help with',
          link: 'help',
        },
        {
          id: 2,
          label: 'Things to know',
          link: 'thingsToKnow',
        },
      ],

      // Things to Know
      thingsToKnow: [
        {
          title: 'It\'s already paid for',
          copy: 'Your employer covers the cost for you and your family members. That means no bills, no co-pays, no surprise fees, and no need to involve your insurance company.',
        },
        {
          title: 'It’s based around you',
          copy: 'You’ll choose how you want to talk: over the phone or using video chat. You’ll get personalized, one-on-one help. And if you ever need anything, you’ll always be able to call us.',
        },
        {
          title: 'It’s confidential',
          copy: 'Your conversations are private, so your employer won’t know who you talked to or what you said.',
        },
        {
          title: 'It’s qualified experts only',
          copy: 'You’ll be connected with someone who’s been vetted, screened, and approved by our team.',
        },
      ],
    };
  },

  computed: {
    ...mapGetters('app', [
      'org',
    ]),

    // About Blocks
    aboutSections() {

      const optionals = {
        // Legal Support
        legal: {
          subtitle: 'Legal advice',
          heading: 'Make legal headaches go away',
          copy: 'Overwhelmed with paperwork and jargon? Our network of attorneys will explain things and point you in the right direction.',
          helpList: [
            'Divorce',
            'Contracts',
            'Power of attorney',
            'Wills',
            'Civil judgment',
            'Real estate',
            'Custody issues',
            'Bankruptcy',
            'Traffic violations',
            'Criminal matters',
            'Adoption',
            'Car accidents',
          ],
          iconList: [
            {
              icon: 'message',
              copy: [
                'One hour of consulting for family matters',
              ],
            },
            {
              icon: 'message',
              copy: [
                '30 minutes of consultation for other legal advice',
              ],
            },
            {
              icon: 'discount',
              copy: [
                '25% off attorney fees for most cases and 35% off for family law',
              ],
            },
          ],
          link: {
            label: 'Find legal advice',
            link: 'legal',
          },
          image: images.legal,
        },
        // Financial Planning
        financial: {
          subtitle: 'Financial Planning',
          heading: 'Get your finances in order',
          copy: 'Tackle debts and understand your financial opportunities with help from our network of CPAs, financial planners, budget specialists, and licensed securities brokers.',
          helpList: [
            'Taxes',
            'Retirement planning',
            'Loans',
            'Debt management',
            'Identity theft',
            'Bankruptcy',
            'Saving for college',
            'Budgeting',
            'Child support',
            'Estate planning',
          ],
          iconList: [
            {
              icon: 'man',
              copy: [
                'Get matched with the right financial planner',
              ],
            },
            {
              icon: 'message',
              copy: [
                'Get one hour of financial consulting',
              ],
            },
            {
              icon: 'star',
              copy: [
                'Plan for college or retirement',
              ],
            },
          ],
          
          link: {
            label: 'Start planning',
            link: 'financial',
          },
          image: images.financial,
          
        },
        
        // Family Care
        family: {
          subtitle: 'Family care',
          heading: 'Look after your loved ones',
          copy: 'If you need a little help with big responsibilities, we can connect you with expert advice, care providers, and resources to help care for your whole family, especially:',
          helpList: [
            'Child care',
            'Adult care',
            'Adoption',
            'Pet care',
            'Parenting',
            'Education',
            'Dependents with special needs',
          ],
          iconList: [
            {
              icon: 'woman',
              copy: [
                'Find child care and adult care providers',
              ],
            },
            {
              icon: 'boy',
              copy: [
                'Get support for family with special needs',
              ],
            },
            {
              icon: 'phone',
              copy: [
                '24/7 assistance by phone or online chat',
              ],
            },
          ],
          link: {
            label: 'Care for your family',
            link: 'family',
          },
          image: images.family,
        },
      };

      const sections = [
        // Emotional Support
        {
          subtitle: 'Emotional support',
          heading: 'Get the right help from the right person',
          copy: 'There’s no one-size-fits-all solution to feeling better. That’s why our network includes counselors who specialize in different things but all consider the many parts of your life that affect your emotions.',
          helpList: [
            'Anxiety',
            'Depression',
            'Life transitions',
            'Relationships',
            'Trauma and loss',
            'Substance misuse',
            'Stress',
            'Sleep issues',
            'Tobacco use',
            'Eating disorders',
            'Conflict resolution',
          ],
          iconList: [
            {
              hidden: false,
              icon: 'person',
              copy: [
                'Get matched with the right counselor for you',
              ],
            },
            
            {
              hidden: this.org.is_no_of_sessions_hidden,
              icon: 'video-5',
              copy: [
                `Get ${this.org.number_of_sessions} free session${this.org.number_of_sessions > 1 ? 's' : '' } ${this.org.session_frequency}`,
              ],
            },
            {
              hidden: false,
              icon: 'phone',
              copy: [
                '24/7 assistance by phone',
              ],
            },
          ],
          image: images.emotional,
        },
      ];

      for (const section in optionals) {
        const navs = this.org.homepage_navs || [];
        for(const navItem of navs) {
          if(`/${section}` === navItem.url) {
            sections.push(optionals[section]);
          }
        }
      }

      return sections;
    },
  },

  methods: {
    scrollToDiv(item) {
      let element = document.getElementById(item.link);

      this.selected = item.id;

      if (item.link === 'overview') {
        element =   document.getElementById('app');
      }

      element.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
    },

    handleScroll() {
      const headerOffset = (el) =>  el - 150;

      arrayLoop(this.menu, (index, item) => {
        const section = item.link;
        const element = this.$refs[section].getBoundingClientRect();

        if(headerOffset(element.top) <= 0 && -element.height < headerOffset(element.top) ) {
          this.selected = item.id;
        }
      });
    },
  },

  // Created
  created () {
    window.addEventListener('scroll', this.handleScroll);
  },

  // Destroyed
  destroyed () {
    window.removeEventListener('scroll', this.handleScroll);
  },
};
</script>



// WEBPACK FOOTER //
// About.vue?73e5ccbc